// Razorpay Standard Checkout loader + launcher — mirrors cashfree-sdk.ts's role
// for the INR Razorpay flow. Unlike Cashfree (an npm ESM package), Razorpay ships
// only a global CDN script (window.Razorpay), so we inject it once and memoize
// the promise; on failure we drop the cache so a retry re-fetches instead of
// failing forever. Card data is entered inside Razorpay's hosted modal — never
// our DOM — so this file only ever handles order ids and the public key_id.

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

// The success payload Razorpay hands to the checkout `handler` callback.
export interface RazorpayCheckoutSuccess {
  razorpay_payment_id: string
  razorpay_order_id: string
  razorpay_signature: string
}

export interface RazorpayCheckoutOptions {
  key: string // public key_id (rzp_test_.. / rzp_live_..) — safe in the browser
  order_id: string
  amount: number // in paise
  currency?: string
  name?: string
  description?: string
  customer_id?: string // cust_.. — required to save the card under the customer
  remember_customer?: boolean // true enables the save-card feature at checkout (docs)
  save?: 0 | 1 // NOT in the official Checkout Options table — testing empirically
  recurring?: 0 | 1 // 1 registers the card for future server-side charges
  prefill?: { name?: string; email?: string; contact?: string }
  notes?: Record<string, string>
  handler: (response: RazorpayCheckoutSuccess) => void
  modal?: { ondismiss?: () => void }
  // Razorpay display config (used here to force a card-only modal).
  config?: unknown
  [key: string]: unknown
}

export interface RazorpayInstance {
  open: () => void
  on: (event: string, handler: (payload: unknown) => void) => void
}

type RazorpayConstructor = new (options: RazorpayCheckoutOptions) => RazorpayInstance

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor
  }
}

let razorpayPromise: Promise<RazorpayConstructor> | null = null

// Both Razorpay SDKs — checkout.js (Standard) and razorpay.js (Custom) — define
// the SAME global, `window.Razorpay`, with DIFFERENT constructors: Standard's
// instances expose open(), Custom's expose createPayment(). Whichever script
// loaded last owns the global, so a session that touches both (e.g. Custom
// Checkout for card, then the Standard modal for another method) must not read
// `window.Razorpay` to decide what it got — doing so hands one flow the other
// SDK's constructor and its method is simply missing at call time.
//
// Rule: each loader captures the global inside ITS OWN script's load handler, and
// the two injections are serialised through `sdkLoadChain` so their handlers can
// never interleave. Deliberately no `if (window.Razorpay) resolve(...)` fast
// path — that is exactly the shortcut that returns the wrong SDK.
let sdkLoadChain: Promise<unknown> = Promise.resolve()

const loadRazorpaySdk = <T>(src: string, label: string): Promise<T> => {
  const run = () =>
    new Promise<T>((resolve, reject) => {
      const onLoad = () => {
        // Read synchronously in this script's own load handler: at this instant
        // `window.Razorpay` is the constructor THIS script just installed.
        const ctor = window.Razorpay as unknown as T | undefined
        if (ctor) resolve(ctor)
        else reject(new Error(`${label} loaded but window.Razorpay is undefined`))
      }
      const onError = () => reject(new Error(`Failed to load ${label}`))
      // A tag can already exist after a failed resolve dropped the memo. Attach
      // to it rather than injecting twice; if it had already finished loading the
      // event won't re-fire, so the caller sees a rejection on the surrounding
      // timeout rather than a silently wrong constructor. Correctness over a
      // convenience that produced B2.
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
      const script = existing ?? document.createElement('script')
      script.addEventListener('load', onLoad, { once: true })
      script.addEventListener('error', onError, { once: true })
      if (!existing) {
        script.src = src
        script.async = true
        document.body.appendChild(script)
      }
    })
  // Chain on both fulfil and reject so one failed load can't wedge the other.
  const next = sdkLoadChain.then(run, run)
  sdkLoadChain = next.catch(() => undefined)
  return next
}

// Load (once) and return the Standard Checkout (checkout.js) constructor.
// Memoized so warming it on modal-open and using it on Pay share one fetch.
export const getRazorpay = (): Promise<RazorpayConstructor> => {
  if (razorpayPromise) return razorpayPromise
  razorpayPromise = loadRazorpaySdk<RazorpayConstructor>(
    RAZORPAY_CHECKOUT_SRC,
    'Razorpay checkout.js',
  ).catch((error) => {
    razorpayPromise = null // drop cache so the next attempt re-fetches
    throw error
  })
  return razorpayPromise
}

// Card-only display config: a single Card block with the default method blocks
// suppressed, so the modal never shows UPI / netbanking / wallet.
export const RAZORPAY_CARD_ONLY_CONFIG = {
  display: {
    blocks: {
      cards: { name: 'Pay using Card', instruments: [{ method: 'card' }] },
    },
    sequence: ['block.cards'],
    preferences: { show_default_blocks: false },
  },
}

// Netbanking-only counterpart. Restricting the modal is not cosmetic: the order
// is priced for the method we sent (payment_method=netbanking is fee-free, card
// carries the gateway fee), so letting the customer switch method inside the
// modal would charge the wrong amount. Standard Checkout has no top-level
// `method` option — display.blocks is the supported way to pin one.
export const RAZORPAY_NETBANKING_ONLY_CONFIG = {
  display: {
    blocks: {
      banks: { name: 'Pay using Netbanking', instruments: [{ method: 'netbanking' }] },
    },
    sequence: ['block.banks'],
    preferences: { show_default_blocks: false },
  },
}

// Open the hosted Razorpay checkout modal (card-only by default). Success and
// dismiss are delivered via `options` (handler / modal.ondismiss). `onFailure`
// is attached as the `payment.failed` listener BEFORE open() so no failure
// event can be missed. Returns the instance.
export const launchRazorpayCheckout = async (
  options: RazorpayCheckoutOptions,
  onFailure?: (payload: unknown) => void,
): Promise<RazorpayInstance> => {
  const Razorpay = await getRazorpay()
  const rzp = new Razorpay({
    ...options,
    config: options.config ?? RAZORPAY_CARD_ONLY_CONFIG,
  })
  if (onFailure) rzp.on('payment.failed', onFailure)
  rzp.open()
  return rzp
}

// ── Custom Checkout (razorpay.js) — EVALUATION variant, PCI-gated ──────────────
// Custom Checkout uses a DIFFERENT script (razorpay.js, not checkout.js) and
// collects the card in OUR OWN inputs, passing the raw PAN/CVV to createPayment().
// That puts card data in the console DOM (PCI-DSS SAQ A-EP) and Razorpay gates
// raw-card createPayment behind PCI certification — so on a non-PCI merchant it
// renders the fields but returns payment.error. This is the DEFAULT card flow for
// every INR account; checkout.js (Standard) is still live for saved-card charges
// and for the netbanking delegation the raw-card path can't serve.
const RAZORPAY_CUSTOM_SRC = 'https://checkout.razorpay.com/v1/razorpay.js'

export interface RazorpayCustomCardInput {
  number: string
  name: string
  expiry_month: string
  expiry_year: string
  cvv: string
}

// createPayment data for a card payment. amount is in paise.
export interface RazorpayCustomCardPayment {
  amount: number
  currency?: string
  order_id: string
  email?: string
  contact?: string
  method: 'card'
  card: RazorpayCustomCardInput
  customer_id?: string
  save?: 0 | 1
}

interface RazorpayCustomInstance {
  createPayment: (data: RazorpayCustomCardPayment) => void
  on: (event: string, handler: (payload: unknown) => void) => void
}
type RazorpayCustomConstructor = new (options: {
  key: string
  redirect?: boolean
}) => RazorpayCustomInstance

let razorpayCustomPromise: Promise<RazorpayCustomConstructor> | null = null

// Load razorpay.js (the custom-checkout lib) and return ITS constructor. Shares
// loadRazorpaySdk with getRazorpay() so the two scripts — which both define
// window.Razorpay — can never hand each other the wrong constructor. A session
// can legitimately need both (Custom for card, Standard for another method), so
// this must not assume one variant per session.
export const getRazorpayCustom = (): Promise<RazorpayCustomConstructor> => {
  if (razorpayCustomPromise) return razorpayCustomPromise
  razorpayCustomPromise = loadRazorpaySdk<RazorpayCustomConstructor>(
    RAZORPAY_CUSTOM_SRC,
    'razorpay.js',
  ).catch((error) => {
    razorpayCustomPromise = null
    throw error
  })
  return razorpayCustomPromise
}

// Whether a `payment.error` payload is the customer backing out of 3DS rather
// than a payment that failed.
//
// It matters because redirect:false means a cancelled authentication never
// navigates anywhere — it arrives on the SAME channel as every genuine decline.
// Without this the console tells someone who chose to cancel that their card was
// refused, which is untrue and alarming on a payment screen.
//
// Matched on `reason` (the structured marker) with a description fallback, because
// Razorpay has shipped the cancellation without a reason. Deliberately NOT matched
// on `source: 'customer'` or `step: 'payment_authentication'` — a wrong OTP carries
// both, so widening to those would reclassify real declines as cancellations and
// hide failures the customer needs to see.
export const isUserCancelledPayment = (payload: unknown): boolean => {
  const error = (payload as { error?: { reason?: string; description?: string } } | null)?.error
  if (!error) return false
  if (error.reason === 'payment_cancelled') return true
  return /cancelled by user/i.test(error.description ?? '')
}

// Submit a raw-card payment via Custom Checkout. redirect:false keeps 3DS in-page
// (event-driven). Wires success/error listeners before createPayment.
export const submitRazorpayCustomCardPayment = async (
  key: string,
  payment: RazorpayCustomCardPayment,
  cb: {
    onSuccess: (resp: RazorpayCheckoutSuccess) => void
    onError: (payload: unknown) => void
  },
): Promise<void> => {
  const RazorpayCustom = await getRazorpayCustom()
  const rzp = new RazorpayCustom({ key, redirect: false })
  rzp.on('payment.success', (resp) => cb.onSuccess(resp as RazorpayCheckoutSuccess))
  rzp.on('payment.error', (payload) => cb.onError(payload))
  rzp.createPayment(payment)
}
